import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Mail, Building2, Banknote } from "lucide-react";

import { useClaimChildren, type ClaimRow } from "@/admin/hooks/use-claims";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { useFormatting } from "@/admin/hooks/use-formatting";
import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { Button } from "@/shared/components/ui/button";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { WorkflowActionMenu } from "@/shared/components/workflows/workflow-action-menu";
import { CreateChildClaimDialog } from "./create-child-claim-dialog";
import { BulkEmailDialog } from "./bulk-email-dialog";
import {
  BulkSettleDialog,
  type BulkSettleClaim,
} from "./bulk-settle-dialog";

/** A served decimal string as a number, or null when the line has none. */
function money(value: string | undefined): number | null {
  return value == null || value === "" ? null : Number(value);
}

/**
 * Property lines of a group claim, worked from the parent.
 *
 * One row per property line: where it is, its status, how much was approved,
 * and the line's own workflow actions (its decision buttons), so a reviewer
 * decides every property without opening each line. A row still opens the
 * line's own page.
 *
 * Mounted only for a group claim (gated by the detail page) - every
 * label/colour comes from the claim row's backend-supplied
 * ``claim_state`` / ``claim_state_color``.
 */
export function ChildClaimsTab({
  parentUuid,
  defaultPayoutMode = "combined",
  combinedPayee = "",
  settledAsGroup = false,
  acceptsNewLines = true,
  groupUuid,
  childClaimType,
}: {
  parentUuid: string;
  /** Default payout mode from the parent's intake settlement preference. */
  defaultPayoutMode?: "combined" | "per_claim";
  /** Default combined payee (e.g. the group's name). */
  combinedPayee?: string;
  /**
   * The parent settles as one total (its workflow makes the one payment and
   * its close settles the lines), so a hand-keyed Bulk Settle here would pay
   * the same properties a second time.
   */
  settledAsGroup?: boolean;
  /** False once the parent is past review: no more properties can be added. */
  acceptsNewLines?: boolean;
  /** The claim's property group, to pick from its registrations. */
  groupUuid?: string;
  /** The claim type each property line is filed as (from the umbrella config). */
  childClaimType?: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatCurrency } = useFormatting();
  const { can } = usePermissions();
  // Bulk settle records and processes payments, so it needs the same
  // capability as recording one (WPS-175 #11); the server refuses without it.
  const canSettle = can("claims.pay_claim") && !settledAsGroup;
  const { data: children = [], isLoading, isFetching } = useClaimChildren(parentUuid);
  const [addOpen, setAddOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);

  const settleClaims: BulkSettleClaim[] = useMemo(
    () =>
      children.map((c) => ({
        uuid: c.uuid,
        label: c.product_name || c.consumer_account_address || c.claim_number || "-",
        ownerName: c.consumer_account_name || undefined,
      })),
    [children],
  );

  const qc = useQueryClient();
  const openCount = useMemo(
    () => children.filter((c) => !c.closed_at).length,
    [children],
  );
  // A claim settled as one total: what each property costs, how much of it
  // its own registration covers, and what it pays (served with the lines).
  const totals = useMemo(
    () =>
      children.reduce(
        (sum, c) => ({
          cost: sum.cost + (money(c.settlement_total) ?? 0),
          pays: sum.pays + (money(c.payable_total) ?? 0),
        }),
        { cost: 0, pays: 0 },
      ),
    [children],
  );
  const refreshLines = () => {
    void qc.invalidateQueries({ queryKey: ["claim-children", parentUuid] });
  };

  const columns: DataTableColumn<ClaimRow>[] = [
    {
      id: "unit",
      header: "Property",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-[13px] text-[var(--ink)]">
            {row.property_address ||
              row.consumer_account_address ||
              row.product_name ||
              "-"}
          </span>
          {(row.property_address || row.consumer_account_address) &&
            row.product_name && (
            <span className="text-[12px] text-[var(--ink-2)]">
              {row.product_name}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "owner",
      header: "Homeowner",
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {row.consumer_account_name || "-"}
        </span>
      ),
    },
    {
      id: "state",
      header: "State",
      cell: (row) =>
        row.claim_state ? (
          <StatusBadge status={row.claim_state} color={row.claim_state_color} />
        ) : (
          <span className="text-[12px] text-[var(--ink-3)]">-</span>
        ),
    },
    ...(settledAsGroup
      ? ([
          {
            id: "cost",
            header: t("claims.properties.cost", "Cost"),
            cell: (row) => {
              const cost = money(row.settlement_total);
              return (
                <span className="text-[13px] tabular-nums text-[var(--ink-2)]">
                  {cost == null ? "-" : formatCurrency(cost)}
                </span>
              );
            },
          },
          {
            id: "covered",
            header: t("claims.properties.covered", "Covered"),
            cell: (row) => {
              const cost = money(row.settlement_total);
              const pays = money(row.payable_total);
              return (
                <span className="text-[13px] tabular-nums text-[var(--ink-2)]">
                  {cost && pays != null
                    ? `${Math.round((pays / cost) * 100)}%`
                    : "-"}
                </span>
              );
            },
          },
          {
            id: "pays",
            header: t("claims.properties.pays", "Pays"),
            cell: (row) => {
              const pays = money(row.payable_total);
              return (
                <span className="text-[13px] font-medium tabular-nums text-[var(--ink)]">
                  {pays == null ? "-" : formatCurrency(pays)}
                </span>
              );
            },
          },
        ] satisfies DataTableColumn<ClaimRow>[])
      : []),
    {
      id: "approved",
      header: "Approved",
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {[row.approved_quantity, row.approved_quantity_detail]
            .filter(Boolean)
            .join(" - ") || "-"}
        </span>
      ),
    },
    {
      id: "decision",
      header: "Decision",
      cell: (row) =>
        row.workflow_instance_uuid ? (
          // The line's own actions (e.g. approve full or partial, deny), run
          // here with their own capture dialogs. Clicks stay in the row.
          <div onClick={(e) => e.stopPropagation()}>
            <WorkflowActionMenu
              instanceUuid={row.workflow_instance_uuid}
              inlineLimit={2}
              buttonSize="sm"
              onActionExecuted={refreshLines}
            />
          </div>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Summary + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-[12px] text-[var(--ink-2)]">
          <span className="flex items-center gap-1.5">
            <Building2 size={14} className="text-[var(--ink-3)]" />
            {children.length} propert{children.length !== 1 ? "ies" : "y"}
          </span>
          <span className="text-[var(--ink-2)]">{openCount} open</span>
          {settledAsGroup && children.length > 0 && (
            <span
              className="text-[var(--ink-2)]"
              data-testid="group-properties-total"
            >
              {t("claims.properties.total", {
                defaultValue: "Cost {{cost}} · Pays {{pays}}",
                cost: formatCurrency(totals.cost),
                pays: formatCurrency(totals.pays),
              })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEmailOpen(true)}
            disabled={children.length === 0}
            className="cursor-pointer gap-1.5"
          >
            <Mail size={14} />
            Email Contacts
          </Button>
          {canSettle && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSettleOpen(true)}
              disabled={children.length === 0}
              className="cursor-pointer gap-1.5"
            >
              <Banknote size={14} />
              Bulk Settle
            </Button>
          )}
          {acceptsNewLines && childClaimType && (
            <Button
              size="sm"
              onClick={() => setAddOpen(true)}
              className="cursor-pointer gap-1.5"
            >
              <Plus size={14} />
              Add Property
            </Button>
          )}
        </div>
      </div>

      <DataTable
        tableId="group-child-claims"
        columns={columns}
        data={children}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey="uuid"
        onRowClick={(row) => navigate(`/claims/${row.uuid}`)}
        emptyMessage="No properties on this claim yet. Add the affected properties."
      />

      {acceptsNewLines && childClaimType && (
        <CreateChildClaimDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          parentUuid={parentUuid}
          claimType={childClaimType}
          groupUuid={groupUuid}
          excludeHoldingUuids={children.map((c) => c.holding_uuid).filter(Boolean)}
        />
      )}
      <BulkEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        parentUuid={parentUuid}
        recipientCount={children.length}
      />
      {canSettle && (
        <BulkSettleDialog
          open={settleOpen}
          onOpenChange={setSettleOpen}
          parentUuid={parentUuid}
          claims={settleClaims}
          defaultPayoutMode={defaultPayoutMode}
          defaultCombinedPayee={combinedPayee}
        />
      )}
    </div>
  );
}
